import { css } from 'lit';

export const campaignsStyles = css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-left {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-subtitle {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      /* ─── Campaign Cards Grid ─── */
      .campaigns-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
        gap: var(--space-5);
      }

      .campaign-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        padding: var(--space-6);
        border-radius: var(--radius-xl);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        cursor: default;
        position: relative;
        overflow: hidden;
      }

      .campaign-card:hover {
        transform: translateY(-3px);
        box-shadow: var(--shadow-hover-lift);
      }

      /* Card Header */
      .campaign-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
      }

      .campaign-name {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .pulse-dot {
        display: inline-block;
        width: 6px;
        height: 6px;
        border-radius: var(--radius-full);
        background: var(--color-success);
        animation: softPulse 2.5s ease-in-out infinite;
      }

      /* Progress Bar */
      .progress-section {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .progress-bar-track {
        flex: 1;
        height: 8px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        overflow: hidden;
      }

      .progress-bar-fill {
        height: 100%;
        border-radius: var(--radius-full);
        background: var(--color-primary);
        transition: width var(--duration-slow) var(--ease-out);
      }

      .progress-bar-fill.complete {
        background: var(--color-success);
      }

      .progress-count {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        white-space: nowrap;
      }

      /* Stats Row */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-3);
      }

      .mini-stat {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-tertiary);
        border-radius: var(--radius-md);
      }

      .mini-stat-icon {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        width: 16px;
        text-align: center;
      }

      .mini-stat-icon.success { color: var(--color-success); }
      .mini-stat-icon.error { color: var(--color-error); }
      .mini-stat-icon.warning { color: var(--color-warning); }
      .mini-stat-icon.neutral { color: var(--color-text-tertiary); }

      .mini-stat-info {
        display: flex;
        flex-direction: column;
      }

      .mini-stat-value {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        line-height: 1;
      }

      .mini-stat-label {
        font-family: var(--font-body);
        font-size: 0.625rem;
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      /* Card Footer */
      .campaign-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: var(--space-3);
        border-top: 1px solid var(--color-border-subtle);
      }

      .campaign-meta {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }

      .campaign-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .campaign-meta-item i {
        font-size: var(--text-xs);
      }

      .campaign-actions {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .action-btn {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .action-btn.danger:hover {
        background: var(--color-error-light);
        color: var(--color-error);
      }

      /* ─── Detail View ─── */
      .detail-view {
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      .back-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        background: none;
        border: none;
        cursor: pointer;
        padding: var(--space-1) 0;
        margin-bottom: var(--space-4);
        transition: color var(--transition-fast);
      }

      .back-btn:hover {
        color: var(--color-text-primary);
      }

      .detail-header {
        margin-bottom: var(--space-6);
      }

      .detail-header-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .detail-title-row {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .detail-title {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .detail-progress {
        max-width: 480px;
      }

      /* KPI Cards */
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-8);
      }

      .kpi-card {
        padding: var(--space-6);
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
      }

      .kpi-card .metric-value {
        font-size: var(--text-2xl);
      }

      .kpi-icon {
        width: 36px;
        height: 36px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-base);
        margin-bottom: var(--space-3);
      }

      .kpi-icon.primary {
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .kpi-icon.success {
        background: var(--color-success-light);
        color: var(--color-success);
      }

      .kpi-icon.accent {
        background: var(--color-accent-light);
        color: var(--color-accent);
      }

      .kpi-icon.warning {
        background: var(--color-warning-light);
        color: var(--color-warning);
      }

      /* Results Table */
      .table-container {
        overflow-x: auto;
        border-radius: var(--radius-xl);
        border: 1px solid rgba(255, 255, 255, 0.35);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .status-cell {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .sentiment-positive { color: var(--color-success); }
      .sentiment-negative { color: var(--color-error); }
      .sentiment-neutral { color: var(--color-text-tertiary); }

      /* Settings Tab */
      .settings-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-5);
      }

      .setting-group {
        padding: var(--space-5);
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
      }

      .setting-group-title {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        margin-bottom: var(--space-4);
        letter-spacing: var(--tracking-tight);
      }

      .setting-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) 0;
        border-bottom: 1px solid var(--color-border-subtle);
      }

      .setting-row:last-child {
        border-bottom: none;
      }

      .setting-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .setting-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
      }

      /* Contact List Tab */
      .contacts-summary {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        margin-bottom: var(--space-5);
      }

      .contacts-summary-stat {
        display: flex;
        flex-direction: column;
        gap: var(--space-0\.5);
      }

      .contacts-summary-stat .value {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
      }

      .contacts-summary-stat .label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .contacts-divider {
        width: 1px;
        height: 36px;
        background: var(--color-border-primary);
      }

      /* ─── Responsive ─── */
      @media (max-width: 920px) {
        .campaigns-grid {
          grid-template-columns: 1fr;
        }
        .stats-row {
          grid-template-columns: repeat(2, 1fr);
        }
        .kpi-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .settings-grid {
          grid-template-columns: 1fr;
        }
      }
`;
